import { format, parseISO } from "date-fns";
import { Check, Paperclip, Plus, Receipt } from "lucide-react";
import { useNotify, useTranslate, useUpdate } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { LoadMore } from "../../operations/LoadMore";
import { useInfiniteRecords } from "../../operations/useInfiniteRecords";
import { useDateLocale } from "../../personal/useDateLocale";
import { toDateOnly } from "../../providers/commons/financeSchedule";
import type { FinanceExpense } from "../../types";
import { FinanceListToolbar } from "../FinanceListToolbar";
import {
  EXPENSE_VIEWS,
  getExpenseViewFilter,
  parseView,
} from "../financeViews";
import { GenerateMonthButton } from "../GenerateMonthButton";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { useFormatMoney } from "../useFormatMoney";
import { useCategoryLabel } from "./useCategoryLabel";
import { ExpenseDialog } from "./ExpenseDialog";

const ExpenseStatus = ({ expense }: { expense: FinanceExpense }) => {
  const translate = useTranslate();
  if (expense.paid_at) {
    return <Badge>{translate("crm.finance.expenses.statuses.paid")}</Badge>;
  }
  const isLate = expense.due_date < toDateOnly(new Date());
  return (
    <Badge variant={isLate ? "destructive" : "outline"}>
      {translate(
        isLate
          ? "crm.finance.expenses.statuses.late"
          : "crm.finance.expenses.statuses.to_pay",
      )}
    </Badge>
  );
};

/** Payables: quick filters, server pagination, "mark as paid" */
export const ExpenseList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const formatMoney = useFormatMoney();
  const categoryLabel = useCategoryLabel();
  const { dateFnsLocale } = useDateLocale();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"), EXPENSE_VIEWS);
  const q = searchParams.get("q") ?? "";
  const filter = useMemo(
    () => ({
      ...getExpenseViewFilter(view, new Date()),
      ...(q ? { q } : {}),
    }),
    [view, q],
  );
  const sort = useMemo(
    () =>
      ({
        field: "due_date",
        order: view === "paid" || view === "all" ? "DESC" : "ASC",
      }) as const,
    [view],
  );
  const {
    records: expenses,
    total,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteRecords<FinanceExpense>("finance_expenses", filter, sort);
  const [editing, setEditing] = useState<FinanceExpense | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [update] = useUpdate();

  const markPaid = (expense: FinanceExpense) =>
    update(
      "finance_expenses",
      {
        id: expense.id,
        data: { paid_at: new Date().toISOString() },
        previousData: expense,
      },
      { onSuccess: () => notify("crm.finance.expenses.marked_paid"), onError },
    );
  const day = (value: string) =>
    format(parseISO(value), "P", { locale: dateFnsLocale });

  const markPaidButton = (expense: FinanceExpense) =>
    expense.paid_at ? null : (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={(event) => {
          event.stopPropagation();
          markPaid(expense);
        }}
        aria-label={translate("crm.finance.expenses.mark_paid")}
      >
        <Check />
        <span className="hidden lg:inline">
          {translate("crm.finance.expenses.mark_paid")}
        </span>
      </Button>
    );

  return (
    <div className="flex flex-col gap-3">
      <FinanceListToolbar
        views={EXPENSE_VIEWS}
        view={view}
        labelPrefix="crm.finance.expenses.views"
        actions={
          <>
            <GenerateMonthButton />
            <Button
              type="button"
              onClick={() => setIsCreating(true)}
              aria-label={translate("crm.finance.expenses.create")}
            >
              <Plus />
              <span className="hidden md:inline">
                {translate("crm.finance.expenses.create")}
              </span>
            </Button>
          </>
        }
      />
      {isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : total === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <Receipt className="size-8" />
          {translate(
            view === "all" && !q
              ? "crm.finance.expenses.empty"
              : "crm.finance.empty_filtered",
          )}
        </div>
      ) : isMobile ? (
        <ul className="flex flex-col gap-2">
          {expenses.map((expense) => (
            <li
              key={expense.id}
              className="flex flex-col gap-2 rounded-lg border p-3"
              onClick={() => setEditing(expense)}
              data-testid="expense-row"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">{expense.description}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {categoryLabel(expense.category)}
                    {expense.supplier && ` · ${expense.supplier}`}
                  </p>
                </div>
                <p className="font-semibold whitespace-nowrap">
                  {formatMoney(expense.amount)}
                </p>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <ExpenseStatus expense={expense} />
                  {day(expense.due_date)}
                </span>
                {markPaidButton(expense)}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate("crm.finance.fields.description")}
                </TableHead>
                <TableHead>
                  {translate("crm.finance.expenses.fields.category")}
                </TableHead>
                <TableHead>
                  {translate("crm.finance.expenses.fields.supplier")}
                </TableHead>
                <TableHead>
                  {translate("crm.finance.fields.due_date")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.finance.fields.amount")}
                </TableHead>
                <TableHead>{translate("crm.finance.fields.status")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow
                  key={expense.id}
                  className="cursor-pointer"
                  onClick={() => setEditing(expense)}
                  data-testid="expense-row"
                >
                  <TableCell className="max-w-56 truncate font-medium">
                    <span className="flex items-center gap-1">
                      {expense.description}
                      {(expense.files ?? []).length > 0 && (
                        <Paperclip className="size-3 text-muted-foreground" />
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {categoryLabel(expense.category)}
                  </TableCell>
                  <TableCell className="max-w-40 truncate text-muted-foreground">
                    {expense.supplier}
                  </TableCell>
                  <TableCell className="text-xs">
                    {day(expense.due_date)}
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    {formatMoney(expense.amount)}
                  </TableCell>
                  <TableCell>
                    <ExpenseStatus expense={expense} />
                  </TableCell>
                  <TableCell className="text-right">
                    {markPaidButton(expense)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <LoadMore
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
      <ExpenseDialog
        open={isCreating || editing != null}
        expense={editing}
        onClose={() => {
          setIsCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
};
